/**
 * Subscription purchase helpers.
 *
 * Flow: App → Supabase Edge Function (create-checkout) → Stripe Checkout Session
 *       → User pays on Stripe-hosted page → Webhook updates subscription + profile tier
 */

import { Platform, Alert } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import { supabase, getSupabaseConfig } from '@/lib/supabase';
import { getStripePriceId } from '@/constants/stripePrices';
import { captureError } from '@/lib/errorReporting';
import { createLogger } from '@/lib/logger';

const log = createLogger('PURCHASE');

export type PurchasePlan = {
  id: string;
  tier: 'pro' | 'coach_pro' | 'org_pro';
  name: string;
  priceMonthly: number;
  priceYearly: number;
  features: string[];
  comingSoon: boolean;
};

export const PLANS: PurchasePlan[] = [
  {
    id: 'pro',
    tier: 'pro',
    name: 'Pro',
    priceMonthly: 14.99,
    priceYearly: 79,
    features: [
      'Score history & progress trends',
      'Advanced analytics dashboard',
      'Volume trends, muscle balance & PR velocity',
      'Training frequency & rest day analysis',
      'Exercise deep dive with 1RM estimates',
      'PR progression charts for all core lifts',
      '10 curated workout templates',
      'Unlimited custom templates',
      'Full training split analysis',
      'Career stats & personal data export',
    ],
    comingSoon: false,
  },
  {
    id: 'coach',
    tier: 'coach_pro',
    name: 'Coach',
    priceMonthly: 60,
    priceYearly: 480, // $480/yr = $40/mo (33% off monthly)
    features: [
      'Everything in Pro',
      'Tournament Creation',
      'Judge Certification',
      'Team Management Tools',
      'Athlete Insights',
      'Multi-day program builder',
      'Client adherence tracking',
      'Direct client messaging',
    ],
    comingSoon: true,
  },
  {
    id: 'club_starter',
    tier: 'org_pro',
    name: 'Club Starter',
    priceMonthly: 199,
    priceYearly: 0, // No yearly — monthly only for Club tiers
    features: [
      'Everything in Coach',
      'Up to 50 members',
      '2 coach seats',
      'Branded Tournaments',
      'Member management & attendance',
      'Gym Season Standings',
    ],
    comingSoon: true,
  },
  {
    id: 'club_growth',
    tier: 'org_pro',
    name: 'Club Growth',
    priceMonthly: 499,
    priceYearly: 0,
    features: [
      'Everything in Club Starter',
      'Up to 150 members',
      '5 coach seats',
      'Inter-org challenges',
      'Judge Panel Management',
      'Advanced reporting',
    ],
    comingSoon: true,
  },
  {
    id: 'club_performance',
    tier: 'org_pro',
    name: 'Club Performance',
    priceMonthly: 700,
    priceYearly: 0,
    features: [
      'Everything in Club Growth',
      'Up to 500 members',
      '10 coach seats',
      'Advanced squad management',
      'Priority support',
    ],
    comingSoon: true,
  },
  {
    id: 'club_elite',
    tier: 'org_pro',
    name: 'Club Elite',
    priceMonthly: 999,
    priceYearly: 0,
    features: [
      'Everything in Club Growth',
      'Unlimited members',
      'Unlimited coach seats',
      'Custom branding',
      'Priority support',
      'Dedicated account manager',
    ],
    comingSoon: true,
  },
];

export const CLUB_TIERS = [
  { id: 'club_starter', name: 'Starter', members: 'Up to 50', monthly: 199, yearly: 0, coachSeats: 2 },
  { id: 'club_growth', name: 'Growth', members: 'Up to 150', monthly: 499, yearly: 0, coachSeats: 5 },
  { id: 'club_performance', name: 'Performance', members: 'Up to 500', monthly: 700, yearly: 0, coachSeats: 10, comingSoon: true },
  { id: 'club_elite', name: 'Elite', members: 'Unlimited', monthly: 999, yearly: 0, coachSeats: -1 }, // -1 = unlimited
] as const;

function showError(msg: string) {
  if (Platform.OS === 'web') {
    window.alert(msg);
  } else {
    Alert.alert('Error', msg);
  }
}

/**
 * Create a Stripe Checkout Session via the edge function and open it.
 */
export async function startPurchase(
  planId: string,
  userId: string,
  interval: 'monthly' | 'yearly' = 'monthly',
): Promise<boolean> {
  // Native: use RevenueCat (required by App Store 3.1.1)
  if (Platform.OS !== 'web') {
    try {
      const { planIdToRevenueCatProductId, purchaseProduct } = await import('@/lib/revenueCat');
      const productId = planIdToRevenueCatProductId(planId, interval);
      log.info('Starting RevenueCat purchase', { planId, productId, interval });
      return await purchaseProduct(productId);
    } catch (err: any) {
      log.error('RevenueCat purchase error:', err);
      captureError(err, { context: 'startPurchase_native', planId });
      showError(`Purchase failed: ${err.message || 'Unknown error'}`);
      return false;
    }
  }

  // Web: existing Stripe flow
  try {
    log.info('Starting purchase', { planId, interval, userId: userId.slice(0, 8) });

    const priceId = getStripePriceId(planId, interval);
    if (!priceId) {
      showError('Invalid plan selected.');
      return false;
    }

    log.info('Price ID resolved', { priceId });

    // Build return URLs
    const baseUrl = Platform.OS === 'web' ? window.location.origin : 'duel://';
    const successUrl = `${baseUrl}/checkout/success?session_id={CHECKOUT_SESSION_ID}`;
    const cancelUrl = `${baseUrl}/checkout/cancel`;

    // Call edge function directly with fetch to see full error response
    log.info('Calling create-checkout edge function...');
    const { data: { session: authSession } } = await supabase.auth.getSession();
    const { url: supabaseUrl, anonKey } = getSupabaseConfig();

    const resp = await fetch(`${supabaseUrl}/functions/v1/create-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authSession?.access_token || ''}`,
        'apikey': anonKey,
      },
      body: JSON.stringify({
        priceId,
        userId,
        successUrl,
        cancelUrl,
        ...(planId === 'pro' ? { trial_period_days: 7 } : {}),
      }),
    });

    const data = await resp.json().catch(() => null);
    log.info('Edge function response', { status: resp.status, data });

    if (!resp.ok || !data?.url) {
      const errMsg = data?.error || `HTTP ${resp.status}`;
      log.error('Edge function error:', errMsg);
      showError(`Checkout failed: ${errMsg}`);
      return false;
    }

    log.info('Opening checkout URL', { url: data.url });

    // Open Stripe Checkout
    if (Platform.OS === 'web') {
      // Navigate in same window to avoid popup blockers
      window.location.href = data.url;
    } else {
      await WebBrowser.openBrowserAsync(data.url, {
        dismissButtonStyle: 'cancel',
        presentationStyle: WebBrowser.WebBrowserPresentationStyle.FULL_SCREEN,
      });
    }

    return true;
  } catch (err: any) {
    log.error('Purchase error:', err);
    captureError(err, { context: 'startPurchase', planId, interval });
    showError(`Something went wrong: ${err.message || 'Unknown error'}`);
    return false;
  }
}

/**
 * Restore purchases — re-sync subscription status from server.
 * Useful after reinstall or device switch.
 */
export async function restorePurchases(userId: string): Promise<{ restored: boolean; tier: string }> {
  try {
    log.info('Restoring purchases for user', { userId: userId.slice(0, 8) });

    // Try edge function first
    try {
      const { data: fnData, error: fnError } = await supabase.functions.invoke(
        'restore-subscription',
        { body: { userId } },
      );
      if (!fnError && fnData?.tier) {
        log.info('Subscription restored via edge function', { tier: fnData.tier });
        return { restored: fnData.tier !== 'free', tier: fnData.tier };
      }
    } catch {
      log.warn('restore-subscription edge function not available, falling back');
    }

    // Fallback: query profile directly
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('subscription_tier, subscription_expires_at')
      .eq('id', userId)
      .single();

    if (error || !profile) {
      log.error('Failed to fetch profile for restore', { error });
      return { restored: false, tier: 'free' };
    }

    const tier = profile.subscription_tier || 'free';
    const expiresAt = profile.subscription_expires_at;

    // Check if subscription is still valid
    if (tier !== 'free' && expiresAt) {
      const now = new Date();
      const expiry = new Date(expiresAt);
      if (expiry < now) {
        log.info('Subscription expired', { tier, expiresAt });
        return { restored: false, tier: 'free' };
      }
    }

    log.info('Purchases restored', { tier });
    return { restored: tier !== 'free', tier };
  } catch (err: any) {
    log.error('Restore purchases error:', err);
    captureError(err, { context: 'restorePurchases', userId });
    return { restored: false, tier: 'free' };
  }
}
